import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Bot, ChevronDown, Info, Pencil, Shield, Trash2, UserPlus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/shared/feedback'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { useMyPermissions, useTeam, type TeamMember } from '@/hooks/use-settings'
import { usePresenceMap } from '@/hooks/use-presence'
import { PRESENCE_LABEL, presenceOf } from '@/lib/presence'
import { i18n } from '@/lib/i18n'
import { needsExplicitAccess } from '@/lib/channel-access'
import { aiKeys, updateAiBot, useAiBots, useChannelOverrides, type AiBot } from '@/hooks/use-ai'
import { botSourceLine } from '@/lib/ai-credits'
import { AI_PATHS, botPath, isDefaultBot } from '@/lib/ai-khung'
import { scheduleSummary } from '@/components/ai/work-hours-lib'
import { enabledPatch } from '@/pages/ai/staff/staff-lib'
import { SelectableTable, type TableColumn } from '@/pages/integrations/accounts-table'
import { ROLE_LABELS, avatarSrc } from '@/pages/settings/settings-utils'
import { KIND_BADGE, platformOf } from './ai-member-form-lib'
import { AddHumanMemberDialog } from './add-human-member-dialog'
import { DeleteHumanMemberDialog, EditHumanMemberDialog } from './member-dialogs'
import { EmployeeAccountsDialog } from './employee-accounts-dialog'
import { RolesDialog } from './roles-dialog'
import { MemberChannelAccessView } from './member-channel-access-view'

type Kind = 'ai' | 'human'
type Group = 'all' | Kind

/** Một dòng của bảng nhân viên — người (User) và AI (AiBot) chung một danh sách. */
interface MemberRow {
  id: string
  kind: Kind
  name: string
  sub: string
  avatar?: string
  emoji?: string
  role: string
  active: boolean
  statusLabel: string
  channels: string[]
  /** 'online' | 'busy' | 'offline' (trạng thái trực tuyến — AI tự khai, người theo kết nối). */
  onlineStatus?: string
  /** AI: số tiếp nhận đồng thời tối đa mỗi ngày (0 = không tự phân bổ). */
  maxConcurrent?: number
  schedule: string
  createdAt: string
  bot?: AiBot
  human?: TeamMember
}

const ONLINE_DOT: Record<string, string> = { online: 'bg-success', busy: 'bg-warning', offline: 'bg-muted-foreground/40' }

/** Dòng phụ dưới tên nhân viên AI — theo cách tạo. */
function aiSubtitle(b: AiBot): string {
  if (b.description) return b.description
  if (b.kind === 'builtin' && b.effective?.model) {
    // AI hai chế độ: model thật + nguồn + credit/lượt, và báo tạm dừng (hết credit / thiếu key).
    const src = botSourceLine(b.effective)
    const parts = [`Model ${b.effective.model}`, src?.label, b.effective.ready ? null : i18n.t('team:teamMembersPage.subtitle.paused')]
    return parts.filter(Boolean).join(' · ')
  }
  if (b.kind === 'builtin') return b.model ? `Model ${b.model}` : i18n.t('team:teamMembersPage.subtitle.noModelSelected')
  if (b.kind === 'webhook') return i18n.t('team:teamMembersPage.subtitle.webhookAgent')
  return i18n.t('team:teamMembersPage.subtitle.connectedTo', { platform: platformOf(b.kind)?.label ?? KIND_BADGE[b.kind] })
}

/**
 * Đội ngũ → Quản lý nhân viên — NƠI DUY NHẤT quản lý con người (thêm/sửa/khoá/
 * xoá, giao tài khoản kênh, vai trò & quyền) và nhân viên AI, theo mẫu
 * SaleSmartly. Cài đặt không còn mục nhân viên.
 *
 * Dữ liệu ghép từ `/settings/team` và `/ai/bots` — chưa có model chung; AI vẫn
 * được giao theo KÊNH (mỗi kênh một nhân viên AI).
 */
export function TeamMembersPage() {
  const { t } = useTranslation('team')
  const qc = useQueryClient()
  const team = useTeam()
  // Trạng thái trực tuyến nhân viên người (realtime qua presence:changed).
  const presence = usePresenceMap()
  const bots = useAiBots()
  const { data: channelData } = useChannelOverrides()
  const [group, setGroup] = useState<Group>('all')
  const navigate = useNavigate()
  const newAi = () => navigate(AI_PATHS.newBot)
  const [humanOpen, setHumanOpen] = useState(false)
  // ?add=ai (từ Hộp thư đến → "Tạo nhân viên AI") → luồng tạo nhân viên AI (/ai/nhan-vien/moi).
  // ?add=human (trang Bắt đầu → "Mời đồng nghiệp") → mở hộp thoại Thêm nhân viên con người.
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    const add = params.get('add')
    if (add !== 'ai' && add !== 'human') return
    if (add === 'ai') { navigate(AI_PATHS.newBot); return }
    setHumanOpen(true)
    setParams((p) => { p.delete('add'); return p }, { replace: true })
  }, [params, setParams, navigate])
  // Chế độ xem: danh sách nhân viên hoặc "Phân quyền theo kênh" (trước là trang riêng /team/channel-access).
  const { data: myPerms } = useMyPermissions()
  const canViewChannelAccess = !!myPerms?.has('account_access.view')
  // "Không có quyền → không hiện" (BRD §5.3.1): Quản lý / Nhân viên mặc định chỉ có `employees.view` — trước đây vẫn thấy
  // nút Vai trò & quyền / Thêm / Sửa / Xoá của quản trị, bấm vào mới bị 403.
  const canViewRoles = !!myPerms?.has('roles.view')
  const canCreateHuman = !!myPerms?.has('employees.create')
  const canUpdateHuman = !!myPerms?.has('employees.update')
  const canDeleteHuman = !!myPerms?.has('employees.delete')
  // Nhân viên AI: tạo / bật-tắt = POST/PUT /ai/bots → backend đòi `ai.update` (không theo tên vai trò).
  const canEditAi = !!myPerms?.has('ai.update')
  const view: 'members' | 'channels' = canViewChannelAccess && params.get('view') === 'channels' ? 'channels' : 'members'
  const setView = (v: 'members' | 'channels') =>
    setParams((p) => { if (v === 'channels') p.set('view', 'channels'); else p.delete('view'); return p }, { replace: true })
  const [rolesOpen, setRolesOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<TeamMember | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null)
  const [accountsTarget, setAccountsTarget] = useState<TeamMember | null>(null)

  const rows = useMemo<MemberRow[]>(() => {
    const channelName = (id: string) =>
      channelData?.channels.find((c) => c.id === id)?.displayName || t('teamMembersPage.channelRemoved')
    const ai: MemberRow[] = (bots.data?.bots ?? []).map((b) => ({
      id: `ai:${b.id}`,
      kind: 'ai',
      name: b.name,
      sub: aiSubtitle(b),
      emoji: b.avatarEmoji || '🤖',
      avatar: avatarSrc(b.avatarUrl),
      role: t('teamMembersPage.aiRole'),
      active: b.enabled,
      statusLabel: !b.enabled ? t('teamMembersPage.disabledStatus') : PRESENCE_LABEL[b.onlineStatus] ?? PRESENCE_LABEL.online,
      channels: isDefaultBot(b) ? [t('teamMembersPage.allUnassignedChannels'), ...b.channelAccountIds.map(channelName)] : b.channelAccountIds.map(channelName),
      onlineStatus: b.onlineStatus,
      maxConcurrent: b.maxConcurrentDaily,
      schedule: scheduleSummary({
        scheduleEnabled: b.scheduleEnabled, scheduleStartHour: b.scheduleStartHour, scheduleEndHour: b.scheduleEndHour,
        scheduleDaytimeMode: b.scheduleDaytimeMode, scheduleNighttimeMode: b.scheduleNighttimeMode, defaultMode: b.defaultMode ?? 'suggest',
      }),
      createdAt: b.createdAt,
      bot: b,
    }))
    const humans: MemberRow[] = (team.data ?? []).map((m) => ({
      id: `u:${m.id}`,
      kind: 'human',
      name: m.fullName,
      sub: m.email,
      avatar: avatarSrc(m.avatarUrl),
      role: m.roleName || ROLE_LABELS[m.role],
      active: m.status === 'active',
      statusLabel: m.status === 'active' ? PRESENCE_LABEL[presenceOf(presence.data, m.id)] : t('teamMembersPage.lockedStatus'),
      onlineStatus: presenceOf(presence.data, m.id),
      channels: m.accounts.map((a) => a.displayName || t('teamMembersPage.unnamed')),
      schedule: '—',
      createdAt: m.createdAt,
      human: m,
    }))
    return [...humans, ...ai]
  }, [bots.data, team.data, channelData, presence.data, t])

  const counts = {
    all: rows.length,
    ai: rows.filter((r) => r.kind === 'ai').length,
    human: rows.filter((r) => r.kind === 'human').length,
  }
  const shown = group === 'all' ? rows : rows.filter((r) => r.kind === group)

  async function toggleBot(b: AiBot, enabled: boolean) {
    if (!enabled && isDefaultBot(b) && !window.confirm(t('teamMembersPage.confirmDisableDefaultAi'))) return
    try {
      // Bật/tắt gộp trực tuyến (tắt = ngoại tuyến) — như màn nhân viên AI.
      await updateAiBot(b.id, enabledPatch(enabled, b.onlineStatus))
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const columns: TableColumn<MemberRow>[] = [
    {
      key: 'name',
      header: t('common:fields.staff'),
      cell: (r) => (
        <div className="flex min-w-[200px] max-w-[280px] items-center gap-3">
          {r.kind === 'ai' ? (
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-violet-500/10 text-lg">
              {r.avatar ? <img src={r.avatar} alt="" className="h-full w-full object-cover" /> : r.emoji}
            </span>
          ) : (
            <Avatar className="h-9 w-9">
              {r.avatar && <AvatarImage src={r.avatar} alt={r.name} />}
              <AvatarFallback className="bg-primary/10 text-primary">{initials(r.name)}</AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium" title={r.name}>{r.name}</p>
            <p className="truncate text-xs text-muted-foreground" title={r.sub}>{r.sub}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: t('teamMembersPage.columns.kind'),
      cell: (r) =>
        r.kind === 'ai' ? (
          <Badge className="gap-1 whitespace-nowrap border-transparent bg-violet-500/10 text-violet-700 dark:text-violet-300">
            <Bot className="h-3 w-3" /> {r.bot ? KIND_BADGE[r.bot.kind] ?? 'AI' : 'AI'}
          </Badge>
        ) : (
          <Badge variant="secondary">{t('teamMembersPage.humanBadge')}</Badge>
        ),
    },
    { key: 'role', header: t('common:fields.role'), className: 'whitespace-nowrap', cell: (r) => r.role },
    {
      key: 'channels',
      header: t('teamMembersPage.columns.channels'),
      cell: (r) => {
        // Vai trò xem hết (owner/admin, hoặc phạm vi vai trò = toàn tổ chức) bỏ qua ACL tài khoản → nói rõ thay vì
        // hiện danh sách rỗng. Quản lý KHÔNG thuộc nhóm này (backend data-scope: chỉ kênh của mình + cấp dưới).
        if (r.human && !needsExplicitAccess(r.human)) {
          return <span className="text-xs text-muted-foreground">{t('teamMembersPage.allAccounts')}</span>
        }
        const chips =
          r.channels.length === 0 ? (
          <span className="text-muted-foreground">{r.kind === 'ai' ? t('teamMembersPage.noChannelsAssigned') : t('teamMembersPage.noAccountsAssigned')}</span>
        ) : (
          <div className="flex max-w-[240px] flex-wrap gap-1">
            {r.channels.slice(0, 2).map((c, i) => (
              <Badge key={i} variant="outline" className="max-w-[140px] truncate font-normal">{c}</Badge>
            ))}
            {r.channels.length > 2 && (
              <Badge variant="outline" className="font-normal" title={r.channels.slice(2).join(', ')}>
                +{r.channels.length - 2}
              </Badge>
            )}
          </div>
        )
        if (!r.human) return chips
        // Nhân viên: bấm để giao / gỡ tài khoản kênh (ChannelAccountAccess).
        return (
          <button
            type="button"
            onClick={() => setAccountsTarget(r.human!)}
            title={t('teamMembersPage.clickToAssignAccounts')}
            className="flex items-center gap-1 rounded-md p-1 text-left transition-colors hover:bg-accent"
          >
            {chips}
            <span className="rounded border border-dashed px-1.5 text-[11px] text-primary">+</span>
          </button>
        )
      },
    },
    {
      key: 'status',
      header: t('common:fields.status'),
      className: 'whitespace-nowrap',
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              !r.active
                ? 'bg-muted-foreground/40'
                : r.onlineStatus
                  ? ONLINE_DOT[r.onlineStatus] ?? 'bg-success'
                  : 'bg-success',
            )}
          />
          {r.statusLabel}
        </span>
      ),
    },
    {
      key: 'maxConcurrent',
      header: t('teamMembersPage.columns.maxConcurrent'),
      className: 'whitespace-nowrap',
      cell: (r) =>
        r.kind !== 'ai' ? (
          <span className="text-muted-foreground">—</span>
        ) : r.maxConcurrent ? (
          <span className="tabular-nums">{r.maxConcurrent}</span>
        ) : (
          <span className="text-xs text-muted-foreground" title={t('teamMembersPage.maxConcurrentHint')}>{t('teamMembersPage.noAutoAssign')}</span>
        ),
    },
    { key: 'schedule', header: t('teamMembersPage.columns.schedule'), className: 'whitespace-nowrap', cell: (r) => r.schedule },
    {
      key: 'created',
      header: t('common:fields.createdAt'),
      className: 'whitespace-nowrap',
      cell: (r) => dayjs(r.createdAt).format('DD/MM/YYYY'),
    },
    {
      key: 'actions',
      header: t('common:fields.actions'),
      sticky: true,
      className: 'w-px whitespace-nowrap text-right',
      cell: (r) =>
        r.kind === 'ai' && r.bot ? (
          <div className="flex items-center justify-end gap-2">
            {/* Không có `ai.update` → không hiện công tắc (trạng thái đã có ở cột Trạng thái), nút chỉ để xem. */}
            {canEditAi && (
              <Switch
                checked={r.bot.enabled}
                onCheckedChange={(v) => toggleBot(r.bot!, v)}
                aria-label={r.bot.enabled ? t('teamMembersPage.disableAi') : t('teamMembersPage.enableAi')}
              />
            )}
            <Button asChild variant="outline" size="sm">
              <Link to={botPath(r.bot.id)}>
                {canEditAi ? <><Pencil /> {t('common:actions.edit')}</> : t('common:actions.view')}
              </Link>
            </Button>
          </div>
        ) : r.human && (canUpdateHuman || canDeleteHuman) ? (
          <div className="flex items-center justify-end gap-1">
            {canUpdateHuman && (
              <Button variant="ghost" size="sm" onClick={() => setEditTarget(r.human!)}>
                <Pencil /> {t('common:actions.edit')}
              </Button>
            )}
            {canDeleteHuman && r.human.role !== 'owner' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setDeleteTarget(r.human!)}
                aria-label={t('teamMembersPage.deleteMemberLabel')}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        ) : null,
    },
  ]

  const GROUPS: { value: Group; label: string }[] = [
    { value: 'all', label: t('teamMembersPage.groups.all') },
    { value: 'ai', label: t('teamMembersPage.aiRole') },
    { value: 'human', label: t('teamMembersPage.groups.human') },
  ]

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-medium">{t('teamMembersPage.title')}</h1>

      {/* Gộp "Phân quyền theo kênh" vào cùng trang (30/09/2026) — cùng một bảng ChannelAccountAccess
          với cột "Kênh phụ trách", chỉ khác chiều xem: theo nhân viên hoặc theo tài khoản kênh. */}
      {canViewChannelAccess && (
      <Tabs value={view} onValueChange={(v) => setView(v as 'members' | 'channels')}>
        <TabsList>
          <TabsTrigger value="members" className="gap-1.5"><Users className="h-4 w-4" /> {t('common:fields.staff')}</TabsTrigger>
          <TabsTrigger value="channels" className="gap-1.5"><Shield className="h-4 w-4" /> {t('teamMembersPage.tabs.channelAccess')}</TabsTrigger>
        </TabsList>
      </Tabs>
      )}

      {view === 'channels' ? (
        <MemberChannelAccessView />
      ) : (
      <>

      <div className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-4 py-2.5 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <p className="text-muted-foreground">
          {t('teamMembersPage.aiIntro')}
        </p>
      </div>

      {/* Nhóm nhân viên dạng tab — trang đã có menu phụ Đội ngũ bên trái, thêm
          một cột nữa thì bảng bị ép hẹp. */}
      <Tabs value={group} onValueChange={(v) => setGroup(v as Group)}>
        <TabsList>
          {GROUPS.map((g) => (
            <TabsTrigger key={g.value} value={g.value} className="gap-1.5">
              {g.label}
              <span className="tabular-nums text-muted-foreground">{counts[g.value]}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div>
          <SelectableTable
            key={group}
            rows={shown}
            rowKey={(r) => r.id}
            columns={columns}
            loading={team.isLoading || bots.isLoading}
            error={team.isError && bots.isError}
            itemLabel={t('teamMembersPage.itemLabel')}
            searchPlaceholder={t('teamMembersPage.searchPlaceholder')}
            searchText={(r) => `${r.name} ${r.sub} ${r.role}`}
            filters={[
              { value: 'active', label: t('teamMembersPage.filters.active'), match: (r) => r.active },
              { value: 'inactive', label: t('teamMembersPage.filters.inactive'), match: (r) => !r.active },
            ]}
            toolbarExtra={
              <>
              {canViewRoles && (
                <Button variant="outline" onClick={() => setRolesOpen(true)}>
                  <Shield /> {t('teamMembersPage.rolesAndPermissions')}
                </Button>
              )}
              {(canCreateHuman || canEditAi) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <UserPlus /> {t('teamMembersPage.addMember')} <ChevronDown className="opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {canCreateHuman && (
                    <DropdownMenuItem onClick={() => setHumanOpen(true)}>
                      <Users className="mr-2 h-4 w-4" /> {t('teamMembersPage.groups.human')}
                    </DropdownMenuItem>
                  )}
                  {canEditAi && (
                    <DropdownMenuItem onClick={newAi}>
                      <Bot className="mr-2 h-4 w-4" /> {t('teamMembersPage.aiRole')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
              </>
            }
            empty={
              <EmptyState
                icon={group === 'ai' ? Bot : Users}
                title={group === 'ai' ? t('teamMembersPage.empty.aiTitle') : t('teamMembersPage.empty.humanTitle')}
                description={
                  group === 'ai'
                    ? t('teamMembersPage.empty.aiDesc')
                    : t('teamMembersPage.empty.humanDesc')
                }
                action={
                  (group === 'ai' ? canEditAi : canCreateHuman) && (
                    <Button onClick={() => (group === 'ai' ? newAi() : setHumanOpen(true))}>
                      <UserPlus /> {group === 'ai' ? t('teamMembersPage.createAi') : t('teamMembersPage.addMember')}
                    </Button>
                  )
                }
              />
            }
          />
      </div>
      </>
      )}

      <AddHumanMemberDialog open={humanOpen} onOpenChange={setHumanOpen} />
      <EditHumanMemberDialog member={editTarget} onClose={() => setEditTarget(null)} />
      <DeleteHumanMemberDialog member={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <EmployeeAccountsDialog member={accountsTarget} onClose={() => setAccountsTarget(null)} />
      <RolesDialog open={rolesOpen} onOpenChange={setRolesOpen} />
    </div>
  )
}
